// Helper functions for checking user roles and permissions

export function getStoredCurrentUser() {
  const raw = localStorage.getItem("currentUser");
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    // What failed, and nothing of it: JSON.parse's error quotes the text it couldn't read (1.6's 3.7).
    console.error("Error parsing the stored current user");
    return null;
  }
}

/**
 * Get user data from JWT token
 * @returns {Object|null} User data object or null if no token
 */
export function getUserFromToken() {
  const token = localStorage.getItem("token");
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));

    // payload.exp is seconds since epoch (JWT standard); Date.now() is ms. An expired token
    // still decodes fine - only the signature is invalid, which this client-side check can't
    // verify anyway - so without this, hasPermission()/hasRole() would keep reporting whatever
    // was granted at login long after the session is actually over (up to the token's 10-hour
    // life), with menus and route guards never reflecting that the session ended. Same
    // stale-claim mechanism as the backend/frontend permission-grant asymmetry already recorded
    // in docs/decisions.md, from the expiry direction instead of the new-grant direction.
    if (typeof payload.exp === "number" && payload.exp * 1000 <= Date.now()) {
      return null;
    }

    const storedUser = getStoredCurrentUser();
    return {
      username: payload.sub || payload.username,
      accountId: payload.accountId ?? null,
      roles: payload.roles || storedUser?.roles || [],
      permissions: payload.permissions || storedUser?.permissions || [],
      authorities: payload.authorities || storedUser?.authorities || [],
      profile: storedUser,
    };
  } catch {
    console.error("Error parsing the stored token");
    return null;
  }
}

function getClaimValues(claim) {
  if (typeof claim === "string") {
    return claim
      .split(/[\s,]+/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (!Array.isArray(claim)) {
    return [];
  }

  return claim
    .map((item) => {
      if (typeof item === "string") {
        return item;
      }

      if (item && typeof item === "object") {
        return (
          item.authority ||
          item.name ||
          item.value ||
          item.roleName ||
          item.permissionName ||
          ""
        );
      }

      return "";
    })
    .filter(Boolean);
}

function getRolePermissionClaims(roles) {
  if (!Array.isArray(roles)) {
    return [];
  }

  return roles.flatMap((role) => getClaimValues(role?.permissions || []));
}

function getNormalizedUserClaims(user) {
  if (!user) {
    return [];
  }

  return Array.from(
    new Set([
      ...getClaimValues(user.roles),
      ...getClaimValues(user.permissions),
      ...getClaimValues(user.authorities),
      ...getRolePermissionClaims(user.roles),
      ...getClaimValues(user.profile?.roles),
      ...getClaimValues(user.profile?.permissions),
      ...getClaimValues(user.profile?.authorities),
      ...getRolePermissionClaims(user.profile?.roles),
    ])
  );
}

function getMatchingServiceRole(value, knownServiceRoles) {
  const normalizedValue = String(value || "").replace(/^ROLE_/, "");

  return (
    knownServiceRoles.find(
      (serviceRole) =>
        normalizedValue === serviceRole ||
        normalizedValue.startsWith(`${serviceRole}_`)
    ) || null
  );
}

/**
 * Check if user has a specific role
 * @param {string} role - Role name to check (e.g., 'PARISH', 'SUNDAY_SCHOOL')
 * @returns {boolean}
 */
export function hasRole(role) {
  const user = getUserFromToken();
  if (!user) return false;
  const normalizedRole = String(role || "").replace(/^ROLE_/, "");

  return getNormalizedUserClaims(user).some((value) => {
    const normalizedValue = String(value).replace(/^ROLE_/, "");
    return (
      normalizedValue === normalizedRole ||
      normalizedValue.startsWith(`${normalizedRole}_`)
    );
  });
}

/**
 * Check if user has any of the specified roles
 * @param {Array<string>} roles - Array of role names
 * @returns {boolean}
 */
export function hasAnyRole(roles) {
  return roles.some(role => hasRole(role));
}

/**
 * Check if user has all of the specified roles
 * @param {Array<string>} roles - Array of role names
 * @returns {boolean}
 */
export function hasAllRoles(roles) {
  return roles.every(role => hasRole(role));
}

/**
 * Check if user has a specific permission
 * @param {string} permission - Permission name to check (e.g., 'READ', 'WRITE')
 * @returns {boolean}
 */
export function hasPermission(permission) {
  const user = getUserFromToken();
  if (!user) return false;

  return getNormalizedUserClaims(user).includes(permission);
}

/**
 * Check if user has any of the specified permissions
 * @param {Array<string>} permissions - Array of permission names
 * @returns {boolean}
 */
export function hasAnyPermission(permissions) {
  return permissions.some(permission => hasPermission(permission));
}

/**
 * Get user's service roles (PARISH, SUNDAY_SCHOOL, etc.)
 * @returns {Array<string>} Array of service role names
 */
export function getUserServiceRoles() {
  const user = getUserFromToken();
  if (!user) return [];
  
  const serviceRoles = [];
  const knownServiceRoles = ['PARISH', 'SUNDAY_SCHOOL', 'YOUTH', 'CHOIR', 'DEACONS'];
  
  // Check if SUPER_ADMIN (can access all services)
  if (hasRole('SUPER_ADMIN')) {
    return knownServiceRoles; // Return all service types for SUPER_ADMIN
  }
  
  getNormalizedUserClaims(user).forEach((value) => {
    const matchedRole = getMatchingServiceRole(value, knownServiceRoles);
    if (matchedRole && !serviceRoles.includes(matchedRole)) {
      serviceRoles.push(matchedRole);
    }
  });
  
  return serviceRoles;
}

export function getUserServiceRolesFromProfile(userProfile) {
  if (!userProfile) {
    return [];
  }

  const knownServiceRoles = ['PARISH', 'SUNDAY_SCHOOL', 'YOUTH', 'CHOIR', 'DEACONS'];
  const serviceRoles = [];
  const profileClaims = Array.from(
    new Set([
      ...getClaimValues(userProfile.roles),
      ...getClaimValues(userProfile.permissions),
      ...getClaimValues(userProfile.authorities),
      ...getRolePermissionClaims(userProfile.roles),
    ])
  );

  profileClaims.forEach((value) => {
    const matchedRole = getMatchingServiceRole(value, knownServiceRoles);
    if (matchedRole && !serviceRoles.includes(matchedRole)) {
      serviceRoles.push(matchedRole);
    }
  });

  return serviceRoles;
}

/**
 * Get the primary service role for scoped feature filtering
 * @returns {string|null} Primary service role or null
 */
export function getPrimaryServiceRole() {
  const serviceRoles = getUserServiceRoles();
  return serviceRoles.length > 0 ? serviceRoles[0] : null;
}

/**
 * A sidebar link/page a user can't actually reach is a bug, not a minor inconsistency - they
 * click it, the backend correctly 403s, and the page renders broken/empty with no explanation
 * (see docs/decisions.md). This mirrors se.medhanealem.cmms.service.EndpointAuthorizationService
 * .can(domain, "READ") read-for-read so the UI shows exactly what the backend will actually
 * allow, not a coarse guess. Keep this in sync if that switch statement changes - it's a
 * duplicated port, not a shared source of truth, because the two apps don't share code.
 */
const DOMAIN_READ_FALLBACKS = {
  MEMBER: ["SUPER_ADMIN_READ", "ADMIN_READ", "USER_READ", "PARISH_READ", "SUNDAY_SCHOOL_READ", "PRIESTHOOD_READ"],
  DASHBOARD: ["SUPER_ADMIN_READ", "ADMIN_READ", "USER_READ", "PARISH_READ", "SUNDAY_SCHOOL_READ", "PRIESTHOOD_READ"],
  USER_ACCOUNT: ["SUPER_ADMIN_READ", "ADMIN_READ"],
  CHURCH: ["SUPER_ADMIN_READ"], // deliberately no ADMIN fallback - matches the backend
  CHURCH_SERVICE: ["SUPER_ADMIN_READ", "ADMIN_READ"],
  BAPTISM: ["SUPER_ADMIN_READ", "ADMIN_READ"],
  MARRIAGE: ["SUPER_ADMIN_READ", "ADMIN_READ"],
  FITHAT: ["SUPER_ADMIN_READ", "ADMIN_READ"],
  EMPLOYEE: ["SUPER_ADMIN_READ", "ADMIN_READ"],
  MEMBER_RELATION: ["SUPER_ADMIN_READ", "ADMIN_READ"],
  CERTIFICATE: ["SUPER_ADMIN_READ", "ADMIN_READ", "MEMBER_READ", "USER_READ"],
  // Not a backend domain: the church-wide member scope UserAccountServiceUtils.hasChurchWideMemberScope
  // checks for anything that lists or describes other members, or shows consent-text drafts - the
  // completeness worklist and the consent-text settings. MEMBER read alone (which a plain USER holds via
  // USER_READ) is not enough there. Keep in sync with that method.
  MEMBER_RECORDS: ["SUPER_ADMIN_READ", "ADMIN_READ", "MEMBER_SCOPE_CHURCH", "MEMBER_SCOPE_ALL"],
  // Not a backend domain: the member read tiers above one's own record (cmms MemberInfoServiceImpl
  // .resolveScopedMembersForRead; EndpointAuthorizationService.canListClergy). A plain USER's member read is their own
  // record alone, which My Profile shows: the member list and directory are not theirs (the owner, 26 Oct 2026).
  MEMBER_LIST: ["SUPER_ADMIN_READ", "ADMIN_READ", "MEMBER_SCOPE_ALL", "MEMBER_SCOPE_CHURCH", "MEMBER_SCOPE_SERVICE_PARISH",
    "PARISH_READ", "MEMBER_SCOPE_SERVICE_SUNDAY_SCHOOL", "SUNDAY_SCHOOL_READ", "MEMBER_SCOPE_CONFESSION_CHILDREN",
    "PRIESTHOOD_READ"],
  // Not a backend domain: who the dashboard shows anything to (cmms DashboardServiceImpl's parish and Sunday School
  // tiers); to anyone else it answers empty, so it is no one else's page.
  DASHBOARD_STATS: ["SUPER_ADMIN_READ", "ADMIN_READ", "DASHBOARD_SCOPE_PARISH", "MEMBER_SCOPE_CHURCH",
    "MEMBER_SCOPE_SERVICE_PARISH", "PARISH_READ", "DASHBOARD_SCOPE_SUNDAY_SCHOOL", "MEMBER_SCOPE_SERVICE_SUNDAY_SCHOOL",
    "SUNDAY_SCHOOL_READ"],
  // Every other domain (BOOKING, CLERGY_AVAILABILITY, IMPORT, REPORT, MEMBER_MERGE,
  // SERVICE_CATALOG, APPLICATION, AUDIT_LOG, TRANSFER, SERVANT...) has no case in the backend
  // switch either - only the universal SYSTEM_READ shortcut or an exact <DOMAIN>_READ grant it.
};

/**
 * The public pages' staff screens (cmms public-page-plan.md, 4): domains with a write and no read - every route asks
 * canWrite, reads included (drafts are read by who writes them) - so the write opens the screen, as the backend's
 * canWrite does: SYSTEM_WRITE or the domain's write, never SYSTEM_READ (session 7's reviewer pass). Held by SUPER_ADMIN
 * and ADMIN, and PUBLIC_CONTENT_WRITE by ROLE_PUBLIC_EDITOR, CLASS_CALENDAR_WRITE by ROLE_SUNDAY_SCHOOL.
 */
const WRITE_ONLY_DOMAINS = { PUBLIC_CONTENT: "PUBLIC_CONTENT_WRITE", CLASS_CALENDAR: "CLASS_CALENDAR_WRITE" };

/**
 * Can the current user read this domain, per the same rules the backend enforces? `domain` is
 * one of the backend's EndpointAuthorizationService domain strings (e.g. "MEMBER", "BOOKING").
 */
export function canReadDomain(domain) {
  if (!domain) return true; // ungated page (e.g. dashboard boilerplate, welcome) - always allowed
  if (WRITE_ONLY_DOMAINS[domain]) return hasPermission("SYSTEM_WRITE") || hasPermission(WRITE_ONLY_DOMAINS[domain]);
  if (hasPermission("SYSTEM_READ")) return true;
  if (hasPermission(`${domain}_READ`)) return true;
  return (DOMAIN_READ_FALLBACKS[domain] || []).some((permission) => hasPermission(permission));
}

/** Ordered most-specific-prefix-first - checked in order, first match wins. Paths are relative
 * to /app/ (i.e. what routes/index.js and routes/sidebar.js actually use). A path with no match
 * is treated as ungated: /welcome, the account's own pages, and the self-scoped screens marked
 * null below. The template's demo pages (/leads, /calendar, /transactions, /settings-billing,
 * /integration) and its documentation, chart, blank and team pages (removed 15 Oct 2026), and its
 * profile page (removed 16 Oct 2026), used to rely on this fallback - if you add a route, give it a domain below rather than leaning on this. */
const PATH_DOMAIN_PREFIXES = [
  ["/members/completeness", "MEMBER_RECORDS"],
  ["/members/import", "IMPORT"],
  ["/members/duplicates", "MEMBER_MERGE"],
  ["/members/add-relation", "MEMBER_RELATION"],
  ["/members", "MEMBER_LIST"], // the list, the directory and a member's pages: not a plain USER's
  ["/register-member", "MEMBER_LIST"], // a staff form: its clergy list and its write are refused a plain USER
  ["/audit-logs", "AUDIT_LOG"],
  ["/reports", "REPORT"],
  ["/baptisms", "BAPTISM"],
  ["/marriages", "MARRIAGE"],
  ["/fithat", "FITHAT"],
  // Section 5.3: the membership and application consent texts - reading them needs MEMBER read for the
  // church (as the backend does); changing them needs CONSENT_TEXT_WRITE, which the page checks itself.
  ["/settings/consent-texts", "MEMBER_RECORDS"],
  ["/settings/churches", "CHURCH"],
  ["/settings/employees", "EMPLOYEE"],
  ["/settings/users", "USER_ACCOUNT"],
  ["/church-service", "CHURCH_SERVICE"], // matches both /church-service/members and /church-services
  ["/service-applications", "APPLICATION"],
  ["/bookings", "BOOKING"],
  ["/clergy-availability", "CLERGY_AVAILABILITY"],
  ["/service-catalog", "SERVICE_CATALOG"],
  ["/dashboard", "DASHBOARD_STATS"],
  // M12. The administrative screens need ACTIVITY_READ. The list, an activity's page and the
  // attendance sheet are deliberately ungated, like meetings: access there is self-scoped - a group
  // leader who is a plain USER reaches the activities they lead and the sessions they took, decided by
  // the backend per activity and per session (leader access, docs/decisions.md 10 Oct 2026) - so a
  // MEETING-style null entry, not an ACTIVITY gate that no leader holds.
  ["/activities/settings", "ACTIVITY"],
  ["/activities/worklists", "ACTIVITY"],
  ["/activities/new", "ACTIVITY"],
  ["/activities/edit", "ACTIVITY"],
  ["/activities", null],
  ["/public-calendar", "PUBLIC_CONTENT"],
  ["/public-profile", "PUBLIC_CONTENT"],
  ["/class-calendar", "CLASS_CALENDAR"],
];

/** @param {string} path - a route path as used in routes/sidebar.js, e.g. "/app/baptisms" or
 * "/baptisms/register" - the leading "/app" (if present) is stripped before matching. */
export function resolveDomainForPath(path) {
  const normalized = (path || "").replace(/^\/app/, "");
  const match = PATH_DOMAIN_PREFIXES.find(([prefix]) => normalized.startsWith(prefix));
  return match ? match[1] : null;
}

/** Convenience wrapper - true if the current user can reach this route at all. */
export function canAccessPath(path) {
  return canReadDomain(resolveDomainForPath(path));
}

/** Where "back" and "get started" lead: the dashboard for whoever it shows anything to, else the welcome page - never a
 * page the caller is refused (a plain USER was sent from Access denied to Access denied). */
export function startPage() {
  return canAccessPath("/app/dashboard") ? "/app/dashboard" : "/app/welcome";
}

